import * as React from "react";
import { Box, makeStyles } from "@material-ui/core";
import { Colors } from "styles/Colors";
import UserProfileModalHelper from "common/helpers/userProfileModalHelper";
import UserService from "services/UserService";
import UserManager from "common/userManager";
import NameplateService from "services/NameplateService";
import HtmlTooltip from "common/components/HtmlTooltip/HtmlTooltip";

/**
 * @type {Array<(nameplateId: number) => void>}
 */
let updateCardCallbacks = [];

export function updateUserCardsWithNameplateId(nameplateId) {
	for (let callback of updateCardCallbacks) {
		callback(nameplateId);
	}
}

const useStyles = makeStyles((theme) => ({
	root: {
		backgroundColor: Colors.panelLight,
		borderRadius: 12,
		border: "1px solid " + theme.palette.primary.main,
		color: theme.palette.primary.main,
		textAlign: "left",
		display: "inline-flex",
		alignItems: "center",
		width: 143,
		height: 34,
		boxSizing: "border-box",
		whiteSpace: "nowrap",
	},
	name: {
		marginLeft: 11,
		flex: "0 0 99px",
		font: "normal normal bold 14px/24px Arial",
		maxWidth: 99,
		textOverflow: "ellipsis",
		overflow: "hidden",
	},
	rootAutoWidth: {
		width: "auto",
		minWidth: 143,
		maxWidth: "100%",
	},
	nameAutoWidth: {
		flex: "1 1 auto",
		maxWidth: "none",
		minWidth: 0,
		marginRight: 5,
	},
	nameplateName: {
		fontSize: 13,
		fontWeight: "bold",
		color: Colors.secondary,
		marginTop: 12,
	},
	nameplateDescription: {
		fontSize: 13,
		fontStyle: "italic",
		color: Colors.primaryDark,
		marginTop: 2,
	},
	tooltipName: {
		display: "block",
		fontWeight: "bold",
	},
	tooltipDescription: {
		display: "block",
		fontStyle: "italic",
		marginTop: 2,
	},
	pinned: {
		maxHeight: 30,
		marginRight: 3,
		width: "unset",
	},
	pinnedWrapper: {
		display: "flex",
	},
	pinnedAutoWidth: {
		marginLeft: 6,
		marginRight: 5,
	},
}));

export default function UserCard(props) {
	//either takes a userid and loads data, or takes the rest of the information to populate the view.
	const {
		userId,
		nameplateId,
		dontLoadData,
		userName,
		pinnedEventName,
		pinnedTrophyImagePath,
		autoWidth,
		showNameplateDetails,
		chipSuffix,
		preview,
		disableProfileLink,
	} = props;

	const styles = useStyles();
	// undefined = no live update yet, null = live-set to default, number = live-set id
	const [liveNameplateId, setLiveNameplateId] = React.useState(undefined);
	const [userCardInfo, setUserCardInfo] = React.useState(null);

	const updateNameplateId = (newNameplateId) => {
		setLiveNameplateId(newNameplateId ?? null);
	};

	React.useEffect(() => {
		if (!preview && userId === UserManager.getUserId()) {
			updateCardCallbacks.push(updateNameplateId);

			return () => {
				updateCardCallbacks = updateCardCallbacks.filter(
					(callback) => callback !== updateNameplateId
				);
			};
		}
	});

	React.useEffect(() => {
		if (!dontLoadData) {
			UserService.getUserCardInfo(userId).then((userInfo) => {
				setUserCardInfo(userInfo);
			});
		}
	}, [userId, dontLoadData]);

	const pinnedTrophy = !!userCardInfo ? userCardInfo.pinnedTrophy[0] : null;
	const pinnedTrophyName = !!pinnedTrophy ? pinnedTrophy.name : ``;
	const pinnedTrophyImg = !!pinnedTrophy
		? pinnedTrophy.imagePath
		: pinnedTrophyImagePath;
	const pinnedTrophyEvent = !!pinnedTrophy
		? pinnedTrophy.eventName
		: pinnedEventName;
	const uName = !!userCardInfo ? userCardInfo?.username : userName;
	const hasPinnedTrophy = !!pinnedTrophy || pinnedTrophyImagePath;
	const userNameplateId =
		liveNameplateId !== undefined
			? liveNameplateId
			: nameplateId ?? userCardInfo?.nameplateId;
	const nameplateIdClassName = `nameplate-${userNameplateId}`;
	const nameplate = NameplateService.getById(userNameplateId);
	const hasNameplateInfo = !!(nameplate?.name || nameplate?.description);

	const card = (
		<Box
			className={`${styles.root} ${autoWidth ? styles.rootAutoWidth : ""} ${
				userNameplateId ? nameplateIdClassName : ""
			} nameplate-container`}
			style={{ marginBottom: props.marginBottom }}
			onClick={
				preview || disableProfileLink
					? undefined
					: () => UserProfileModalHelper.openProfile(userId)
			}
		>
			<span
				className={`${styles.name} ${
					autoWidth ? styles.nameAutoWidth : ""
				} nameplate-name`}
			>
				{uName}
			</span>
			{hasPinnedTrophy && (
				<span
					title={`${pinnedTrophyEvent}`}
					className={`${styles.pinnedWrapper} ${
						autoWidth ? styles.pinnedAutoWidth : ""
					} nameplate-trophy`}
				>
					<img
						className={styles.pinned}
						alt={`${pinnedTrophyName}`}
						src={pinnedTrophyImg}
					/>
				</span>
			)}
		</Box>
	);

	if (showNameplateDetails) {
		return (
			<div>
				{card}
				{chipSuffix}
				{nameplate?.name && (
					<div className={styles.nameplateName}>{nameplate.name}</div>
				)}
				{nameplate?.description && (
					<div className={styles.nameplateDescription}>
						{nameplate.description}
					</div>
				)}
			</div>
		);
	}

	if (!preview && hasNameplateInfo) {
		return (
			<HtmlTooltip
				title={
					<>
						{nameplate.name && (
							<span className={styles.tooltipName}>{nameplate.name}</span>
						)}
						{nameplate.description && (
							<span className={styles.tooltipDescription}>
								{nameplate.description}
							</span>
						)}
					</>
				}
			>
				{card}
			</HtmlTooltip>
		);
	}

	return card;
}
